import { STEP_MIN, canStepDown, canStepUp, stepLimitText, stepperValueNow } from '../services/login-limit-stepper';

interface NumberStepperProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  decreaseLabel: string;
  increaseLabel: string;
  invalid?: boolean;
  disabled?: boolean;
  describedBy?: string;
}

/**
 * Whole-number field with − / + buttons, used instead of native spinners
 * because Safari and Firefox draw and size those differently. ArrowUp and
 * ArrowDown step from the field; the buttons stay out of the tab order
 * (spinbutton pattern). Steps never go below 1, and an empty value is kept
 * until the admin types or steps. In RTL the row mirrors, so − sits on the right.
 */
export function NumberStepper({ id, value, onChange, decreaseLabel, increaseLabel, invalid, disabled, describedBy }: NumberStepperProps) {
  const step = (delta: 1 | -1) => {
    const next = stepLimitText(value, delta);
    if (next !== value) onChange(next);
  };

  return (
    <div className="number-stepper">
      <button
        type="button"
        className="number-stepper__btn"
        tabIndex={-1}
        aria-label={decreaseLabel}
        aria-controls={id}
        disabled={disabled || !canStepDown(value)}
        onClick={() => step(-1)}
      >
        <span aria-hidden="true">−</span>
      </button>
      <input
        id={id}
        className={`form-input number-stepper__input${invalid ? ' form-input--invalid' : ''}`}
        role="spinbutton"
        aria-valuemin={STEP_MIN}
        aria-valuenow={stepperValueNow(value)}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        inputMode="numeric"
        dir="ltr"
        disabled={disabled}
        value={value}
        onBeforeInput={(event) => {
          const data = event.data;
          if (data != null && data.length === 1 && /\D/.test(data)) event.preventDefault();
        }}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
          event.preventDefault();
          step(event.key === 'ArrowUp' ? 1 : -1);
        }}
        onChange={(event) => onChange(event.target.value)}
      />
      <button
        type="button"
        className="number-stepper__btn"
        tabIndex={-1}
        aria-label={increaseLabel}
        aria-controls={id}
        disabled={disabled || !canStepUp(value)}
        onClick={() => step(1)}
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  );
}
